import { getAccessToken, onTokenChange, refreshSession } from './api';

/**
 * Realtime client.
 *
 * One socket per browser tab, shared by every panel. Topics are reference
 * counted, so a component can subscribe and unsubscribe freely without cutting
 * off a sibling that needs the same stream.
 *
 * The reconnect policy matters more here than in a typical app: a dispatcher
 * staring at a stale queue believes it is current. Every reconnect therefore
 * fires a `reconnected` signal, and panels refetch on it rather than assuming
 * they only missed nothing.
 */

export type Topic = 'incidents' | 'units' | 'calls' | 'messages' | 'presence' | 'system';

export interface RealtimeEvent {
  topic: Topic;
  type: string;
  payload: unknown;
  at: string;
}

export type ConnectionState = 'connecting' | 'open' | 'closed';

type EventHandler = (event: RealtimeEvent) => void;
type StateHandler = (state: ConnectionState, reconnected: boolean) => void;

const topicRefs = new Map<Topic, number>();
const eventHandlers = new Set<EventHandler>();
const stateHandlers = new Set<StateHandler>();

let socket: WebSocket | null = null;
let state: ConnectionState = 'closed';
let attempt = 0;
let reconnectTimer: number | null = null;
let everConnected = false;
let intentionallyClosed = false;

function setState(next: ConnectionState, reconnected = false): void {
  state = next;
  for (const handler of stateHandlers) handler(next, reconnected);
}

export function connectionState(): ConnectionState {
  return state;
}

function send(message: unknown): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

function activeTopics(): Topic[] {
  return [...topicRefs.entries()].filter(([, count]) => count > 0).map(([topic]) => topic);
}

function scheduleReconnect(): void {
  if (intentionallyClosed || reconnectTimer !== null) return;
  // Exponential backoff to 15s, with jitter so a server restart does not bring
  // every console back in the same millisecond.
  const delay = Math.min(15_000, 500 * 2 ** attempt) * (0.7 + Math.random() * 0.6);
  attempt += 1;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    void connect();
  }, delay);
}

export async function connect(): Promise<void> {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

  let token = getAccessToken();
  if (!token) {
    // The socket authenticates with the access token in the query string, so a
    // page opened with only a refresh cookie has to mint one first.
    if (!(await refreshSession())) return;
    token = getAccessToken();
    if (!token) return;
  }

  intentionallyClosed = false;
  setState('connecting');

  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const ws = new WebSocket(`${protocol}://${window.location.host}/ws?token=${encodeURIComponent(token)}`);
  socket = ws;

  ws.onopen = () => {
    const reconnected = everConnected;
    everConnected = true;
    attempt = 0;
    setState('open', reconnected);
    const topics = activeTopics();
    if (topics.length) send({ action: 'subscribe', topics });
  };

  ws.onmessage = (message) => {
    let frame: { kind?: string; topic?: Topic; type?: string; payload?: unknown; at?: string };
    try {
      frame = JSON.parse(message.data as string);
    } catch {
      return;
    }
    if (frame.kind !== 'event' || !frame.topic || !frame.type) return;
    const event: RealtimeEvent = {
      topic: frame.topic,
      type: frame.type,
      payload: frame.payload,
      at: frame.at ?? new Date().toISOString(),
    };
    for (const handler of eventHandlers) handler(event);
  };

  ws.onclose = (closeEvent) => {
    socket = null;
    setState('closed');
    // 4401 means the token was rejected: refresh once and try again rather
    // than hammering the server with the same bad credential.
    if (closeEvent.code === 4401) {
      void refreshSession().then((ok) => {
        if (ok) scheduleReconnect();
      });
      return;
    }
    scheduleReconnect();
  };

  ws.onerror = () => {
    // onclose always follows; nothing to do here but let it.
  };
}

export function disconnect(): void {
  intentionallyClosed = true;
  everConnected = false;
  attempt = 0;
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  socket?.close(1000, 'signed out');
  socket = null;
  topicRefs.clear();
  setState('closed');
}

/** Subscribes to topics for as long as the returned function is uncalled. */
export function subscribe(topics: Topic[]): () => void {
  const added: Topic[] = [];
  for (const topic of topics) {
    const count = topicRefs.get(topic) ?? 0;
    topicRefs.set(topic, count + 1);
    if (count === 0) added.push(topic);
  }
  if (added.length) send({ action: 'subscribe', topics: added });
  void connect();

  return () => {
    const removed: Topic[] = [];
    for (const topic of topics) {
      const count = (topicRefs.get(topic) ?? 1) - 1;
      topicRefs.set(topic, Math.max(0, count));
      if (count <= 0) removed.push(topic);
    }
    if (removed.length) send({ action: 'unsubscribe', topics: removed });
  };
}

export function onEvent(handler: EventHandler): () => void {
  eventHandlers.add(handler);
  return () => eventHandlers.delete(handler);
}

export function onConnectionChange(handler: StateHandler): () => void {
  stateHandlers.add(handler);
  return () => stateHandlers.delete(handler);
}

// A fresh token (sign-in or rotation) means the socket should carry it too.
onTokenChange((token) => {
  if (token === null) return;
  if (!socket && !intentionallyClosed) void connect();
});
